import jsPDF from "jspdf";
import "jspdf-autotable";
import { format } from "date-fns";
import { parse } from "date-fns";
import { es } from "date-fns/locale";

function formatoPesos(value) {
  return (
    "$ " +
    value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

function FormatoFechaSinHora(value) {
  let fecha_a = value.replace("T", " ");
  let fecha_b = fecha_a.replace("Z", "");
  const fecha = parse(fecha_b, "yyyy-MM-dd HH:mm:ss", new Date());
  return format(fecha, "dd-MMMM-yyyy", { locale: es }).toUpperCase();
}

function FormatoFechaConHora(value) {
  const fecha = new Date(value);
  return format(fecha, "dd-MMMM-yyyy HH:mm:ss", { locale: es }).toUpperCase();
}

function calcularAnchoProporcional(base64, alturaFija) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const ancho =
        img.naturalWidth && img.naturalHeight
          ? (img.naturalWidth * alturaFija) / img.naturalHeight
          : 50;
      resolve(ancho);
    };
    img.onerror = () => resolve(50);
    img.src = base64;
  });
}

function detectarFormatoImagen(base64) {
  if (base64.startsWith("data:image/png")) return "PNG";
  if (base64.startsWith("data:image/jpeg")) return "JPEG";
  if (base64.startsWith("data:image/jpg")) return "JPEG";
  if (base64.startsWith("data:image/gif")) return "GIF";
  if (base64.startsWith("data:image/webp")) return "WEBP";
  return "PNG";
}

const PolizaPDF = async (ObjPoliza, objEmpresa, objColor) => {
  return new Promise(async (resolve, reject) => {
    try {
      const doc = new jsPDF({ format: "letter" });

      const color = objColor || "#662e91";
      const logoBase64 = objEmpresa?.logo?.base64 || "";
      const fechaHoraGeneracion = FormatoFechaConHora(new Date());

      const margenIzq = 12;
      const margenDer = 12;
      const anchoUtil = 215 - margenIzq - margenDer; // ~191mm
      const xDer = 215 - margenDer;

      // ── CABECERA ──────────────────────────────────────────────────
      let y = 14;

      // Logo
      if (logoBase64 !== "") {
        const alturaLogo = 20;
        const formatoImg = detectarFormatoImagen(logoBase64);
        const anchoLogo = await calcularAnchoProporcional(
          logoBase64,
          alturaLogo
        );
        doc.addImage(
          logoBase64,
          formatoImg,
          margenIzq,
          y - 5,
          anchoLogo,
          alturaLogo
        );
      }

      // Título + datos empresa (derecha)
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Póliza", xDer, y, { align: "right" });

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(
        (objEmpresa?.rfc || "") + " | " + (objEmpresa?.nombre || ""),
        xDer,
        (y += 6),
        { align: "right" }
      );
      doc.text(ObjPoliza.tipo + " | " + ObjPoliza.numPoliza, xDer, (y += 5), {
        align: "right",
      });
      doc.text(FormatoFechaSinHora(ObjPoliza.fecha), xDer, (y += 5), {
        align: "right",
      });

      y += 10;

      // ── DESCRIPCIÓN ───────────────────────────────────────────────
      doc.autoTable({
        startY: y,
        head: [[{ content: "Descripción", styles: { halign: "center" } }]],
        body: [
          [
            {
              content: ObjPoliza.descripcion || "",
              styles: { halign: "center" },
            },
          ],
        ],
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: {
          fillColor: color,
          textColor: "#ffffff",
          fontStyle: "bold",
        },
        bodyStyles: { fillColor: "#f9f9f9" },
        margin: { left: margenIzq, right: margenDer },
        tableWidth: anchoUtil,
      });

      y = doc.lastAutoTable.finalY + 6;

      // ── TABLA DE ASIENTOS ─────────────────────────────────────────
      doc.autoTable({
        startY: y,
        head: [
          [
            { content: "Cuenta", styles: { halign: "left" } },
            { content: "Nombre Cuenta", styles: { halign: "left" } },
            { content: "Descripción", styles: { halign: "left" } },
            { content: "Cargo", styles: { halign: "right" } },
            { content: "Abono", styles: { halign: "right" } },
          ],
        ],
        body: ObjPoliza.asientos.map((c) => [
          { content: c.cuenta?.numCuenta || "", styles: { halign: "left" } },
          { content: c.cuenta?.descripcion || "", styles: { halign: "left" } },
          { content: c.descripcion || "", styles: { halign: "left" } },
          { content: formatoPesos(c.cargo), styles: { halign: "right" } },
          { content: formatoPesos(c.abono), styles: { halign: "right" } },
        ]),
        styles: { fontSize: 6, cellPadding: 2, overflow: "linebreak" },
        headStyles: {
          fillColor: color,
          textColor: "#ffffff",
          fontStyle: "bold",
        },
        alternateRowStyles: { fillColor: "#f4f4f4" },
        columnStyles: {
        //   0: { cellWidth: 55 }, // Cuenta
        //   1: { cellWidth: 90 }, // Descripción
          3: { cellWidth: 25 }, // Cargo
          4: { cellWidth: 25 }, // Abono
        },
        margin: { left: margenIzq, right: margenDer },
        tableWidth: anchoUtil,
        // Fila de totales al final
        foot: [
          [
            {
              content: "TOTAL:",
              colSpan: 2,
              styles: {
                halign: "left",
                fontStyle: "bold",
                fillColor: color,
                textColor: "#ffffff",
              },
            },
            {
              content: formatoPesos(ObjPoliza.cargo),
              styles: {
                halign: "right",
                fontStyle: "bold",
                fillColor: color,
                textColor: "#ffffff",
              },
            },
            {
              content: formatoPesos(ObjPoliza.abono),
              styles: {
                halign: "right",
                fontStyle: "bold",
                fillColor: color,
                textColor: "#ffffff",
              },
            },
          ],
        ],
        footStyles: {
          fillColor: color,
          textColor: "#ffffff",
          fontStyle: "bold",
        },
      });

      y = doc.lastAutoTable.finalY + 6;

      // ── TABLA DE CFDIs (listaXml) ─────────────────────────────────
      if (ObjPoliza.listaXml && ObjPoliza.listaXml.length > 0) {
        doc.autoTable({
          startY: y,
          head: [
            [
              { content: "Serie/Folio", styles: { halign: "left" } },
              { content: "Tipo", styles: { halign: "left" } },
              { content: "Fecha", styles: { halign: "left" } },
              { content: "RFC", styles: { halign: "left" } },
              { content: "Nombre", styles: { halign: "left" } },
              { content: "Forma Pago", styles: { halign: "left" } },
              { content: "Método Pago", styles: { halign: "left" } },
              { content: "Moneda", styles: { halign: "left" } },
              { content: "Descuento", styles: { halign: "right" } },
              { content: "SubTotal", styles: { halign: "right" } },
              { content: "Total", styles: { halign: "right" } },
              { content: "Folio Fiscal", styles: { halign: "left" } },
            ],
          ],
          body: ObjPoliza.listaXml.map((c) => [
            {
              content: (c.serie || "") + " | " + (c.folio || ""),
              styles: { halign: "left" },
            },
            { content: c.tipoComprobante || "", styles: { halign: "left" } },
            {
              content:
                FormatoFechaSinHora(c.fecha) +
                "\n" + 'Fecha de Pago '+
                FormatoFechaSinHora(c.fechaPago),
              styles: { halign: "left" },
            },
            { content: c.rfc || "", styles: { halign: "left" } },
            { content: c.nombre || "", styles: { halign: "left" } },
            { content: c.formaPago || "", styles: { halign: "left" } },
            { content: c.metodoPago || "", styles: { halign: "left" } },
            { content: c.moneda || "", styles: { halign: "left" } },
            {
              content: formatoPesos(c.descuento || 0),
              styles: { halign: "right" },
            },
            {
              content: formatoPesos(c.subTotal || 0),
              styles: { halign: "right" },
            },
            {
              content: formatoPesos(c.total || 0),
              styles: { halign: "right" },
            },
            {
              content: c.folioFiscal || "",
              styles: { halign: "left", fontSize: 5 },
            },
          ]),
          styles: { fontSize: 5, cellPadding: 2, overflow: "linebreak" },
          headStyles: {
            fillColor: color,
            textColor: "#ffffff",
            fontStyle: "bold",
          },
          alternateRowStyles: { fillColor: "#f4f4f4" },
          columnStyles: {
            0: { cellWidth: 14 }, // Serie/Folio
            1: { cellWidth: 8 }, // Tipo
            2: { cellWidth: 18 }, // Fecha
            3: { cellWidth: 18 }, // RFC
            4: { cellWidth: 20 }, // Nombre
            5: { cellWidth: 14 }, // Forma Pago
            6: { cellWidth: 14 }, // Método Pago
            7: { cellWidth: 10 }, // Moneda
            8: { cellWidth: 16 }, // Descuento
            9: { cellWidth: 16 }, // SubTotal
            10: { cellWidth: 16 }, // Total
            11: { cellWidth: 27 }, // Folio Fiscal
          },
          margin: { left: margenIzq, right: margenDer },
          tableWidth: anchoUtil,
        });
      }

      // ── PIE DE PÁGINA ─────────────────────────────────────────────
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8);
        doc.setTextColor(150);

        const pageH = doc.internal.pageSize.getHeight();
        const pageW = doc.internal.pageSize.getWidth();

        doc.setDrawColor(color);
        doc.setLineWidth(0.3);
        doc.line(margenIzq, pageH - 16, pageW - margenDer, pageH - 16);

        doc.text("Generado: " + fechaHoraGeneracion, margenIzq, pageH - 8, {
          align: "left",
        });
        doc.text("Documento generado por ContaGo", pageW / 2, pageH - 8, {
          align: "center",
        });
        doc.text(
          "Página " + i + " de " + totalPages,
          pageW - margenDer,
          pageH - 8,
          { align: "right" }
        );
      }

      const base64 = doc.output("datauristring");
      resolve(base64);
    } catch (error) {
      console.error(error);
      reject(error);
    }
  });
};

export { PolizaPDF };
